import axios from 'axios'
import { computed, onUnmounted, unref } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { GetAvailableTimes } from '@/api/reservation'
import { GetAvailableTimeByTotalBookingTime } from '@/api/serviceUnit'

export const availabilityEndpoints = Object.freeze({
  times: 'times',
  totalBookingTime: 'total-booking-time',
})

export const AVAILABILITY_CACHE_TTL = 30 * 1000
export const AVAILABILITY_MAX_RETRIES = 2

// 建立/編輯/取消預約等會改變可預約時段的動作成功後呼叫。
// 用 removeQueries 而非 invalidateQueries：所有 availability query 皆 refetchOnMount: false，
// invalidate 只標記 stale 不會觸發重新請求，元件仍會讀到舊快取。
export const clearAvailabilityQueries = (queryClient) => {
  queryClient.removeQueries({ queryKey: ['availability'] })
}

export const normalizeAvailabilityPayload = (value) => {
  if (value instanceof Date) return value.toISOString()

  if (value && typeof value.toISOString === 'function') return value.toISOString()

  if (Array.isArray(value)) return value.map(normalizeAvailabilityPayload)

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        if (typeof value[key] !== 'undefined') result[key] = normalizeAvailabilityPayload(value[key])
        return result
      }, {})
  }

  return value
}

export const getAvailabilityQueryKey = (endpoint, payload) => {
  const normalizedPayload = normalizeAvailabilityPayload(payload)

  return [
    'availability',
    endpoint,
    normalizedPayload?.shopId ?? null,
    normalizedPayload,
  ]
}

export class AvailabilityQueryError extends Error {
  constructor (responseError, rawError) {
    super(typeof responseError === 'string' ? responseError : responseError?.msg || 'Availability request failed')
    this.name = 'AvailabilityQueryError'
    this.responseError = responseError
    this.rawError = rawError
  }
}

const getResponseStatus = (error) => {
  if (!error || typeof error !== 'object') return null
  return Number(error.status || error.response?.status) || null
}

export const shouldRetryAvailabilityQuery = (failureCount, error) => {
  if (failureCount >= AVAILABILITY_MAX_RETRIES || axios.isCancel(error)) return false

  if (error instanceof AvailabilityQueryError) {
    const status = getResponseStatus(error.rawError)
    if (status >= 500 || error.responseError === 503 || error.responseError === '請求逾時') return true
    return error.responseError === '系統錯誤' && !error.rawError
  }

  if (typeof error === 'string') return ['請求逾時', '系統錯誤'].includes(error)

  const status = getResponseStatus(error)
  return error.code === 'ECONNABORTED' || !error.response || status >= 500
}

const requestByEndpoint = {
  [availabilityEndpoints.times]: GetAvailableTimes,
  [availabilityEndpoints.totalBookingTime]: GetAvailableTimeByTotalBookingTime,
}

const requestAvailability = async (endpoint, payload, signal) => {
  const request = requestByEndpoint[endpoint]
  if (!request) throw new Error(`Unknown availability endpoint: ${endpoint}`)

  const [data, responseError, rawError] = await request({ ...payload, signal })
  if (responseError) throw new AvailabilityQueryError(responseError, rawError)
  return data
}

export const createAvailabilityQueryOptions = ({ endpoint, payload }) => ({
  queryKey: getAvailabilityQueryKey(endpoint, payload),
  queryFn: ({ signal }) => requestAvailability(endpoint, normalizeAvailabilityPayload(payload), signal),
  staleTime: AVAILABILITY_CACHE_TTL,
  gcTime: AVAILABILITY_CACHE_TTL,
  retry: shouldRetryAvailabilityQuery,
  retryDelay: (attempt) => 300 * (2 ** attempt),
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
})

export const useAvailabilityQuery = ({ endpoint, payload, enabled = true }) => {
  const queryClient = useQueryClient()
  const queryKey = computed(() => getAvailabilityQueryKey(endpoint, unref(payload)))
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => requestAvailability(endpoint, queryKey.value[3], signal),
    enabled: computed(() => Boolean(unref(enabled) && unref(payload))),
    staleTime: AVAILABILITY_CACHE_TTL,
    gcTime: AVAILABILITY_CACHE_TTL,
    retry: shouldRetryAvailabilityQuery,
    retryDelay: (attempt) => 300 * (2 ** attempt),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const cancel = () => queryClient.cancelQueries({ queryKey: queryKey.value, exact: true })
  const getCachedData = (nextPayload) => queryClient.getQueryData(
    getAvailabilityQueryKey(endpoint, nextPayload),
  )
  onUnmounted(cancel)

  return { query, queryKey, cancel, getCachedData }
}
